import { useFormikContext } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useHiringTeamUsersFindManyQuery } from 'generated/graphql';
import {
  MultiselectFormField,
  TMultiselectOption,
} from '@spotted-zebra-uk/ui-components';
import { FiltersFormValues } from './ProjectsFilterModal';
import {
  pruneHiringTeamUsers,
  toHiringTeamUserOptions,
} from './ProjectsFilterModal.helpers';
import styles from './ProjectFilterModal.module.scss';

export const HIRING_TEAM_USERS_FIELD_DATA_TEST_ID = 'hiring-team-users-field';

const HiringTeamUsersField = () => {
  const { values, setFieldValue } = useFormikContext<FiltersFormValues>();
  const [search, setSearch] = useState('');

  const selectedCompanyIds = useMemo(
    () => values.companies.map(company => Number(company.value)),
    [values.companies]
  );
  const hasCompanySelected = selectedCompanyIds.length > 0;

  const { data } = useHiringTeamUsersFindManyQuery({
    variables: { companyIds: selectedCompanyIds, search: search || undefined },
    skip: !hasCompanySelected,
  });

  const options = useMemo(
    () => toHiringTeamUserOptions(data?.HiringTeamUsersFindMany),
    [data?.HiringTeamUsersFindMany]
  );

  // Genuine cross-field synchronisation rather than a derived value: the
  // selection has to be narrowed in form state so the tab counter and what is
  // applied agree with what is on screen.
  useEffect(() => {
    const prunedHiringTeamUsers = pruneHiringTeamUsers(
      values.hiringTeamUsers,
      values.companies
    );

    if (prunedHiringTeamUsers.length !== values.hiringTeamUsers.length) {
      setFieldValue('hiringTeamUsers', prunedHiringTeamUsers);
    }
  }, [values.companies, values.hiringTeamUsers, setFieldValue]);

  const handleChange = (newValue: TMultiselectOption<string>[]) => {
    setFieldValue('hiringTeamUsers', newValue);
  };

  return (
    <div
      className={styles.hiringTeamUsersField}
      data-testid={HIRING_TEAM_USERS_FIELD_DATA_TEST_ID}
    >
      <MultiselectFormField
        id="hiringTeamUsers"
        name="hiringTeamUsers"
        label="Hiring team member"
        placeholder="Hiring team member"
        bottomText={
          hasCompanySelected
            ? 'Roles where this user is on the hiring team, directly or via a user group'
            : 'Select a company first'
        }
        isDisabled={!hasCompanySelected}
        onChange={handleChange}
        onInputChange={setSearch}
        value={values.hiringTeamUsers}
        options={options}
        maxMenuHeight={400}
      />
    </div>
  );
};

export default HiringTeamUsersField;
